import {
  AppealHistoryActionNames,
  BoxHistoryActionNames,
  ContractHistoryActionNames,
  HistoryActionData,
  HistoryActionName,
  LeadHistoryActionNames,
  ReservationHistoryActionNames,
  UserHistoryActionNames,
  WarehouseHistoryActionNames,
} from 'entities/History';
import { OpenValidationErrorActionNames } from 'entities/History/model/types';
import { TFunction } from 'i18next';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';

export const actionTitleMapper = (
  actionName: HistoryActionName,
  actionData: HistoryActionData,
  t: TFunction,
): { subject: string; action: string; object?: string } => {
  const { userName = 'N/A', boxName, targetName = 'N/A', warehouseName = 'N/A' } = actionData;

  const actionTitleMap: Record<HistoryActionName, { subject: string; action: string; object?: string }> = {
    [UserHistoryActionNames.login_success]: {
      subject: userName,
      action: t('authorized successfully'),
    },
    [UserHistoryActionNames.login_fail]: {
      subject: userName,
      action: t('authorization fail'),
    },
    [UserHistoryActionNames.password_change]: {
      subject: userName,
      action: t('changed password'),
    },
    [UserHistoryActionNames.new_registration]: {
      subject: userName,
      action: t('registration successfully'),
    },
    [UserHistoryActionNames.registration_fail]: {
      subject: userName,
      action: t('registration fail'),
    },
    [UserHistoryActionNames.user_updated]: {
      subject: userName,
      action: t('changed'),
      object: targetName,
    },
    [BoxHistoryActionNames.box_open]: {
      subject: userName,
      action: t('opened the box'),
      object: boxName,
    },
    [BoxHistoryActionNames.box_close]: {
      subject: userName,
      action: t('closed box'),
      object: boxName,
    },
    [BoxHistoryActionNames.box_open_attempt]: {
      subject: userName,
      action: t('attempt to open box'),
      object: boxName,
    },
    [BoxHistoryActionNames.box_open_long_time]: {
      subject: boxName || 'N/A',
      action: t('opened a long time'),
    },
    [BoxHistoryActionNames.box_unauthorized_access]: {
      subject: boxName || 'N/A',
      action: t('unauthorized access to box'),
    },
    [BoxHistoryActionNames.box_verify_state]: {
      subject: boxName || 'N/A',
      action: t('box verify state'),
    },
    [BoxHistoryActionNames.box_update]: {
      subject: userName,
      action: t('updated a box'),
      object: boxName,
    },
    [BoxHistoryActionNames.box_create]: {
      subject: userName,
      action: t('created a box'),
      object: boxName,
    },
    [WarehouseHistoryActionNames.door_open_attempt]: {
      subject: userName,
      action: t('attempt to open'),
      object: t('{{targetName}} at {{warehouseName}}', { targetName: getLocalizedString(targetName), warehouseName }),
    },
    [WarehouseHistoryActionNames.siren_turned_on]: {
      subject: warehouseName,
      action: t('turned on siren'),
    },
    [ReservationHistoryActionNames.reservation_take_up]: {
      subject: userName,
      action: t('taken up reservation'),
      object: targetName,
    },
    [ReservationHistoryActionNames.reservation_create]: {
      subject: userName,
      action: t('reserved a box'),
      object: boxName,
    },
    [ContractHistoryActionNames.contract_create]: {
      subject: userName,
      action: t('booked a box'),
      object: boxName,
    },
    [AppealHistoryActionNames.appeal_create]: {
      subject: userName,
      action: t('created an appeal'),
    },
    [AppealHistoryActionNames.appeal_creation_fail]: {
      subject: userName,
      action: t('failed to create an appeal'),
    },
    [LeadHistoryActionNames.lead_creation_fail]: {
      subject: userName,
      action: t('failed to create a lead'),
    },
    [LeadHistoryActionNames.lead_create]: {
      subject: userName,
      action: t('created a lead'),
    },
    [OpenValidationErrorActionNames.far_from_warehouse]: {
      subject: t('Error while opening the {{object}}.', {
        object: boxName || t('{{targetName}} at {{warehouseName}}', { targetName: getLocalizedString(targetName), warehouseName }),
      }),
      action: t('No Wi-Fi connection or location is more than 500m from the warehouse'),
      object: boxName || t('{{targetName}} at {{warehouseName}}', { targetName: getLocalizedString(targetName), warehouseName }),
    },
    [OpenValidationErrorActionNames.no_active_or_paid_contracts]: {
      subject: t('Error while opening the {{object}}.', {
        object: boxName || t('{{targetName}} at {{warehouseName}}', { targetName: getLocalizedString(targetName), warehouseName }),
      }),
      action: t('No active or paid contracts'),
    },
    [OpenValidationErrorActionNames.warehouse_closed_holiday]: {
      subject: t('Error while opening the {{object}}.', {
        object: boxName || t('{{targetName}} at {{warehouseName}}', { targetName: getLocalizedString(targetName), warehouseName }),
      }),
      action: t('Warehouse is on holiday today'),
      object: boxName || t('{{targetName}} at {{warehouseName}}', { targetName: getLocalizedString(targetName), warehouseName }),
    },
    [OpenValidationErrorActionNames.warehouse_closed_working_hours]: {
      subject: t('Error while opening the {{object}}.', {
        object: boxName || t('{{targetName}} at {{warehouseName}}', { targetName: getLocalizedString(targetName), warehouseName }),
      }),
      action: t('Warehouse is closed'),
      object: boxName || t('{{targetName}} at {{warehouseName}}', { targetName: getLocalizedString(targetName), warehouseName }),
    },
    [OpenValidationErrorActionNames.contract_not_paid]: {
      subject: t('Error while opening the {{object}}.', {
        object: boxName || t('{{targetName}} at {{warehouseName}}', { targetName: getLocalizedString(targetName), warehouseName }),
      }),
      action: t('Contract is not paid'),
      object: boxName || t('{{targetName}} at {{warehouseName}}', { targetName: getLocalizedString(targetName), warehouseName }),
    },
    [OpenValidationErrorActionNames.contract_not_started]: {
      subject: t('Error while opening the {{object}}.', {
        object: boxName || t('{{targetName}} at {{warehouseName}}', { targetName: getLocalizedString(targetName), warehouseName }),
      }),
      action: t('Contract is not started yet'),
      object: boxName || t('{{targetName}} at {{warehouseName}}', { targetName: getLocalizedString(targetName), warehouseName }),
    },
  };

  return actionTitleMap[actionName];
};
